import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import { Prod01 } from '../../constants/ActionType';
import { uploadDocument } from '../../utils/upload';
import { formatDate } from '../../utils/dateFormat';

// Plan of Care tracking (CCM / PCM / RPM / APCM). The clinician authors the POC elsewhere; the
// portal only stores an UPLOADED COPY with date / author / type (INITIAL or FOLLOW-UP) and reports
// completion per program. A missing POC is a "not on file" flag only - it never blocks billing
// processing, data capture or opportunity generation.

const PROGRAMS = ['CCM', 'PCM', 'RPM', 'APCM'];
const TYPES = [{ key: 'initial', label: 'INITIAL' }, { key: 'followUp', label: 'FOLLOW-UP' }];
const CAN_WRITE = ['HrMedical', 'HrLead', 'HrNonMedical', 'doctor', 'admin', 'adminStaff', 'billing'];
const typeLabel = (t) => (TYPES.find(x => x.key === t) || {}).label || t;

const PlanOfCarePanel = ({ patientId, year, month, onChanged }) => {
    const { user } = useSelector(state => state.auth);
    const canWrite = CAN_WRITE.includes(user?.role);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showDocs, setShowDocs] = useState(false);
    const [program, setProgram] = useState('CCM');
    const [type, setType] = useState('initial');
    const [pocDate, setPocDate] = useState(moment().format('YYYY-MM-DD'));
    const [author, setAuthor] = useState('');
    const [note, setNote] = useState('');
    const [file, setFile] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const res = await axios.get(`${Prod01}/billing/poc/${patientId}?year=${year}&month=${month}`);
            setData(res.data);
        } catch (e) { setError(e?.response?.data?.message || e.message); }
    }, [patientId, year, month]);

    useEffect(() => { load(); }, [load]);

    const run = async (fn, okMsg) => {
        setBusy(true); setError(null); setNotice(null);
        try { await fn(); setNotice(okMsg); await load(); if (onChanged) onChanged(); } catch (e) { setError(e?.response?.data?.message || e.message); } finally { setBusy(false); }
    };

    const submit = () => run(async () => {
        if (!file) throw new Error('Select the uploaded POC copy (file) - the portal stores a copy, it does not author the plan.');
        const up = await uploadDocument(file);
        await axios.post(`${Prod01}/billing/poc/${patientId}`, {
            program, type, pocDate, author, note: note || undefined,
            fileName: up.filename, originalFileName: up.originalName || file.name,
        });
        setFile(null); setNote('');
    }, `${program} ${typeLabel(type)} plan of care stored.`);

    const voidDoc = (id) => run(async () => {
        await axios.post(`${Prod01}/billing/poc/${patientId}/${id}/void`, { reason: 'voided from portal' });
    }, 'Plan of care document voided.');

    if (error && !data) return <div className="alert alert-danger py-1 mt-1 mb-1" style={{ fontSize: 12 }}>{error}</div>;
    if (!data) return null;
    const status = data.status || {};
    const docs = (data.documents || []).filter(d => d.status !== 'void');
    const monthLabel = moment({ year, month: month - 1 }).format('MMMM YYYY');

    return (
        <div className="mt-2">
            <div className="fw-bold" style={{ fontSize: 13 }}>Plan of Care on file (uploaded copies) - status for {monthLabel}</div>
            <div className="d-flex flex-wrap gap-2" style={{ fontSize: 12 }}>
                {PROGRAMS.map(p => {
                    const s = status[p] || {};
                    const ok = s.complete;
                    return (
                        <div key={p} className="p-1 rounded" style={{ minWidth: 150, background: ok ? '#e8f5e9' : '#fff3cd', border: `1px solid ${ok ? '#28a745' : '#ff9800'}` }}>
                            <b>{p}</b> {ok ? 'complete' : 'POC not on file'}
                            <div>Initial: {s.hasInitial ? formatDate(s.initialDate) : 'missing'}</div>
                            <div>Follow-up: {s.hasFollowUp ? `${formatDate(s.followUpDate)}${s.followUpCurrent ? '' : ' (not current)'}` : 'missing'}</div>
                        </div>
                    );
                })}
            </div>
            <div className="text-muted" style={{ fontSize: 11 }}>Missing POC is a flag only - billing processing, data capture and opportunity generation continue.</div>
            {error && <div className="alert alert-danger py-1 mt-1 mb-1" style={{ fontSize: 12 }}>{error}</div>}
            {notice && <div className="alert alert-success py-1 mt-1 mb-1" style={{ fontSize: 12 }}>{notice}</div>}
            {canWrite && (
                <div className="d-flex flex-wrap gap-1 mt-1 align-items-center">
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={program} onChange={e => setProgram(e.target.value)}>{PROGRAMS.map(p => <option key={p}>{p}</option>)}</select>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={type} onChange={e => setType(e.target.value)}>{TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</select>
                    <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={pocDate} max={moment().format('YYYY-MM-DD')} onChange={e => setPocDate(e.target.value)} />
                    <input className="form-control form-control-sm" style={{ width: 160 }} placeholder="Author (Provider / Nurse)" value={author} onChange={e => setAuthor(e.target.value)} />
                    <input type="file" className="form-control form-control-sm" style={{ width: 220 }} onChange={e => setFile(e.target.files[0] || null)} />
                    <input className="form-control form-control-sm" style={{ width: 140 }} placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
                    <button className="btn btn-outline-primary btn-sm text-nowrap" onClick={submit} disabled={busy || !author || !file || !pocDate}>Store POC copy</button>
                </div>
            )}
            <button className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={() => setShowDocs(v => !v)}>{showDocs ? 'Hide' : 'Show'} documents ({docs.length})</button>
            {showDocs && (
                <div style={{ fontSize: 12 }}>
                    {docs.length === 0 && <div className="text-muted">No plan-of-care copies stored.</div>}
                    {docs.map(d => (
                        <div key={d.id || d._id} className="d-flex justify-content-between">
                            <span><b>{d.program}</b> {typeLabel(d.type)} · {formatDate(d.pocDate)} · by {d.author}{d.originalFileName ? ` · ${d.originalFileName}` : ''}{d.recordedByName ? ` · uploaded by ${d.recordedByName}` : ''}</span>
                            {canWrite && <button className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={() => voidDoc(d.id || d._id)} disabled={busy}>void</button>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PlanOfCarePanel;
